import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../hooks/usePermissions";

interface RoleBasedRouteProps {
  requiredPermissions?: string[];
  requiredAnyPermissions?: string[];
  requiredAllPermissions?: string[];
  requiredRole?: string;
  fallbackPath?: string;
  showForbiddenMessage?: boolean;
}

const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({
  requiredPermissions = [],
  requiredAnyPermissions = [],
  requiredAllPermissions = [],
  requiredRole,
  fallbackPath = "/dashboard",
  showForbiddenMessage = false,
}) => {
  const { userProfile, isAuthLoading, isAuthenticated } = useAuth();
  const { can, canAny, canAll } = usePermissions();

  // Show loading while authentication is being determined
  if (isAuthLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace />;
  }

  // If no profile data, redirect to fallback
  if (!userProfile) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check role requirement
  if (requiredRole && userProfile.role?.name !== requiredRole) {
    if (showForbiddenMessage) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Access Forbidden
            </h1>
            <p className="text-gray-600 mb-4">
              This page is only accessible to {requiredRole} users.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // Check individual permissions
  const hasRequiredPermissions = requiredPermissions.every((permission) =>
    can(permission)
  );

  // Check any permissions (OR logic)
  const hasAnyRequiredPermissions =
    requiredAnyPermissions.length === 0 || canAny(requiredAnyPermissions);

  // Check all permissions (AND logic)
  const hasAllRequiredPermissions =
    requiredAllPermissions.length === 0 || canAll(requiredAllPermissions);

  // Determine if user has access
  const hasAccess =
    hasRequiredPermissions &&
    hasAnyRequiredPermissions &&
    hasAllRequiredPermissions;

  // If no access and showing forbidden message
  if (!hasAccess && showForbiddenMessage) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Access Forbidden
          </h1>
          <p className="text-gray-600 mb-4">
            You don't have the required permissions to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // If no access, redirect to fallback
  if (!hasAccess) {
    return <Navigate to={fallbackPath} replace />;
  }

  // User has access, render outlet
  return <Outlet />;
};

// Predefined route configurations for common use cases
export const EnterpriseAdminRoute: React.FC = () => (
  <RoleBasedRoute requiredRole="ROLE_CLIENT_ADMIN" />
);

export const FederationAdminRoute: React.FC = () => (
  <RoleBasedRoute requiredRole="ROLE_FEDERATION_ADMIN" />
);

export const TransporterAdminRoute: React.FC = () => (
  <RoleBasedRoute requiredRole="ROLE_TRANSPORTER_ADMIN" />
);

export const JobManagementRoute: React.FC = () => (
  <RoleBasedRoute
    requiredAnyPermissions={[
      PERMISSIONS.CAN_CREATE_JOB,
      PERMISSIONS.CAN_EDIT_JOB,
      PERMISSIONS.CAN_DELETE_JOB,
    ]}
  />
);

export const TransporterManagementRoute: React.FC = () => (
  <RoleBasedRoute requiredPermissions={[PERMISSIONS.CAN_CRUD_TRANSPORTER]} />
);

export const DriverManagementRoute: React.FC = () => (
  <RoleBasedRoute
    requiredPermissions={[PERMISSIONS.CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER]}
  />
);

export const TruckManagementRoute: React.FC = () => (
  <RoleBasedRoute
    requiredPermissions={[
      PERMISSIONS.CAN_CRUD_TRUCK_TRAILER_FOR_SELF_OR_TRANSPORTER,
    ]}
  />
);

export const FederationManagementRoute: React.FC = () => (
  <RoleBasedRoute requiredPermissions={[PERMISSIONS.CAN_CREATE_FEDERATION]} />
);

export default RoleBasedRoute;
